import React, { useEffect, useState } from "react";
import Layout from "./layout";
import { useParams, Link } from "react-router-dom";
import styled from "styled-components";
import { useCoursesContext } from "../context/courses_context";
import { useCartContext } from "../context/cart_context";
import { Oval } from "react-loader-spinner";
import { FaCalendarAlt, FaClock, FaUser, FaGlobe, FaShoppingCart } from "react-icons/fa";
import parse from "html-react-parser";

const SingleTrainingDetail = () => {
  const { id } = useParams();
  const { fetchSingleCourse, single_course } = useCoursesContext();
  const { addToCart } = useCartContext();
  const [loading, setLoading] = useState(true);
  const [selectedPricing, setSelectedPricing] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      await fetchSingleCourse(id);
      setLoading(false);
    };
    fetchData();
  }, [id, fetchSingleCourse]);

  useEffect(() => {
    if (single_course && single_course.Pricings) {
      setSelectedPricing(single_course.Pricings[0]);
    }
  }, [single_course]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Oval
          height={50}
          width={50}
          color="#0f766e"
          wrapperStyle={{}}
          wrapperClass=""
          visible={true}
          ariaLabel="oval-loading"
          secondaryColor="#99f6e4"
          strokeWidth={2}
          strokeWidthSecondary={2}
        />
      </div>
    );
  }

  const {
    courseID,
    title,
    instructor,
    discountedPrice,
    description,
    what_you_will_learn,
    imageSrc,
    Pricings,
    webinarDate,
    duration,
    areas_covered,
    who_will_benefit,
    instructor_profile,
    why_register,
    background,
  } = single_course;

  const dateTime = new Date(webinarDate);

  const formattedDateTimeEST = dateTime.toLocaleString("en-US", {
    timeZone: "America/New_York",
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });

  const formattedDateTimePST = dateTime.toLocaleString("en-US", {
    timeZone: "America/Los_Angeles",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });

  const handlePricingChange = (e) => {
    const pricing = Pricings.find(
      (pricing) => pricing.id === parseInt(e.target.value)
    );
    setSelectedPricing(pricing);
  };

  function convertMinutes(minutes) {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    if (minutes <= 60) {
      return `${minutes} min`;
    }

    return `${hours} hour${hours > 1 ? "s" : ""} ${remainingMinutes} min`;
  }

  const displayPrice = selectedPricing
    ? selectedPricing.price
    : Pricings?.[0]?.price
    ? Pricings[0].price
    : discountedPrice;

  const contentSections = [
    { key: "description", title: "Description", content: description },
    { key: "why_register", title: "Why Register", content: why_register },
    {
      key: "what_you_will_learn",
      title: "Why Should You Attend",
      content: what_you_will_learn,
    },
    {
      key: "areas_covered",
      title: "Areas Covered in the Webinar Session",
      content: areas_covered,
    },
    {
      key: "who_will_benefit",
      title: "Who Will Benefit",
      content: who_will_benefit,
    },
    {
      key: "instructor_profile",
      title: "Instructor Profile",
      content: instructor_profile,
    },
    { key: "background", title: "Background", content: background },
  ].filter((section) => section.content);

  return (
    <Layout>
      <PageWrapper>
        <HeroBand>
          <div className="hero-inner">
            <span className="badge">Live Training Session</span>
            <h1>{title}</h1>
            <p className="hero-sub">
              Led by{" "}
              <strong>{instructor?.replace(/"/g, "") || "Expert Instructor"}</strong>
            </p>
          </div>
        </HeroBand>

        <MainGrid>
          <ContentColumn>
            <MetaStrip>
              <div className="meta-item">
                <span className="icon">
                  <FaUser />
                </span>
                <div>
                  <span className="label">Instructor</span>
                  <span className="value">
                    {instructor?.replace(/"/g, "") || "—"}
                  </span>
                </div>
              </div>
              <div className="meta-item">
                <span className="icon">
                  <FaGlobe />
                </span>
                <div>
                  <span className="label">Language</span>
                  <span className="value">English</span>
                </div>
              </div>
              <div className="meta-item">
                <span className="icon">
                  <FaCalendarAlt />
                </span>
                <div>
                  <span className="label">Schedule</span>
                  <span className="value">
                    {formattedDateTimeEST?.replace("at", ", ")} EST /{" "}
                    {formattedDateTimePST} PST
                  </span>
                </div>
              </div>
              <div className="meta-item">
                <span className="icon">
                  <FaClock />
                </span>
                <div>
                  <span className="label">Duration</span>
                  <span className="value">
                    {duration ? convertMinutes(duration) : "—"}
                  </span>
                </div>
              </div>
            </MetaStrip>

            <SectionsList>
              {contentSections.map((section, index) => (
                <article key={section.key} className="detail-section">
                  <div className="section-header">
                    <span className="section-index">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <h2>{section.title}</h2>
                  </div>
                  <div className="section-body">{parse(section.content)}</div>
                </article>
              ))}
            </SectionsList>
          </ContentColumn>

          <AsideColumn>
            <EnrollmentCard>
              <div className="thumb-wrap">
                <img src={imageSrc} alt={title || "Training session"} />
              </div>

              <div className="price-block">
                <span className="price-label">Enrollment fee</span>
                <span className="price-amount">${displayPrice}</span>
              </div>

              {Pricings?.length >= 1 && (
                <div className="field">
                  <label htmlFor="sessionType">Session Type</label>
                  <select
                    id="sessionType"
                    name="sessionType"
                    onChange={handlePricingChange}
                    value={selectedPricing ? selectedPricing.id : ""}
                  >
                    {Pricings?.map((pricing) => (
                      <option key={pricing.id} value={pricing.id}>
                        {pricing.sessionType} — ${pricing.price}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="schedule-note">
                <FaCalendarAlt />
                <span>
                  {formattedDateTimeEST?.replace("at", ", ")} EST
                </span>
              </div>

              <Link
                to="/cart"
                className="cart-btn"
                onClick={() =>
                  addToCart(
                    courseID,
                    imageSrc,
                    title,
                    instructor,
                    selectedPricing ? selectedPricing.price : discountedPrice,
                    selectedPricing
                  )
                }
              >
                <FaShoppingCart />
                Add to Cart
              </Link>

              <p className="assurance">
                Secure checkout · Instant confirmation after purchase
              </p>
            </EnrollmentCard>
          </AsideColumn>
        </MainGrid>
      </PageWrapper>
    </Layout>
  );
};

const PageWrapper = styled.div`
  --ink: #0f172a;
  --muted: #64748b;
  --line: #e2e8f0;
  --surface: #ffffff;
  --canvas: #eef6f4;
  --accent: #0f766e;
  --accent-dark: #0d5f59;
  --accent-soft: #ccfbf1;
  position: relative;
  isolation: isolate;
  min-height: 100vh;
  color: var(--ink);
  padding-bottom: 64px;
  background:
    radial-gradient(ellipse 80% 50% at 10% 20%, rgba(45, 212, 191, 0.18), transparent 55%),
    radial-gradient(ellipse 70% 45% at 90% 10%, rgba(14, 116, 144, 0.12), transparent 50%),
    radial-gradient(ellipse 60% 40% at 70% 80%, rgba(15, 118, 110, 0.1), transparent 55%),
    linear-gradient(180deg, #f0fdfa 0%, #eef2ff 45%, #f8fafc 100%);

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    z-index: -1;
    opacity: 0.35;
    background-image:
      linear-gradient(rgba(15, 118, 110, 0.04) 1px, transparent 1px),
      linear-gradient(90deg, rgba(15, 118, 110, 0.04) 1px, transparent 1px);
    background-size: 28px 28px;
    mask-image: linear-gradient(180deg, rgba(0, 0, 0, 0.55), transparent 85%);
  }
`;

const HeroBand = styled.section`
  position: relative;
  overflow: hidden;
  background:
    linear-gradient(125deg, rgba(15, 118, 110, 0.95) 0%, rgba(15, 23, 42, 0.92) 55%, rgba(30, 58, 138, 0.85) 100%);
  color: #f8fafc;
  padding: 48px 20px 72px;

  &::before {
    content: "";
    position: absolute;
    width: 420px;
    height: 420px;
    border-radius: 50%;
    top: -140px;
    right: -80px;
    background: radial-gradient(circle, rgba(94, 234, 212, 0.35), transparent 70%);
    pointer-events: none;
  }

  &::after {
    content: "";
    position: absolute;
    width: 280px;
    height: 280px;
    border-radius: 50%;
    bottom: -100px;
    left: 8%;
    background: radial-gradient(circle, rgba(56, 189, 248, 0.22), transparent 70%);
    pointer-events: none;
  }

  .hero-inner {
    position: relative;
    z-index: 1;
    max-width: 1120px;
    margin: 0 auto;
  }

  .badge {
    display: inline-block;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    background: rgba(255, 255, 255, 0.12);
    border: 1px solid rgba(255, 255, 255, 0.22);
    backdrop-filter: blur(6px);
    padding: 6px 12px;
    margin-bottom: 18px;
  }

  h1 {
    font-size: clamp(1.75rem, 3.5vw, 2.75rem);
    font-weight: 700;
    line-height: 1.2;
    max-width: 720px;
    margin: 0 0 14px;
  }

  .hero-sub {
    margin: 0;
    font-size: 1.05rem;
    color: #cbd5e1;
    max-width: 560px;

    strong {
      color: #fff;
      font-weight: 600;
    }
  }
`;

const MainGrid = styled.div`
  max-width: 1120px;
  margin: -40px auto 0;
  padding: 0 20px;
  display: grid;
  gap: 28px;

  @media (min-width: 980px) {
    grid-template-columns: minmax(0, 1fr) 340px;
    align-items: start;
  }
`;

const ContentColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  min-width: 0;
`;

const MetaStrip = styled.div`
  display: grid;
  gap: 12px;
  grid-template-columns: 1fr;

  @media (min-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }

  .meta-item {
    display: flex;
    gap: 12px;
    align-items: flex-start;
    background: rgba(255, 255, 255, 0.88);
    border: 1px solid rgba(15, 118, 110, 0.1);
    border-radius: 14px;
    padding: 16px 18px;
    box-shadow: 0 4px 16px rgba(15, 23, 42, 0.04);
    backdrop-filter: blur(8px);
    transition: transform 0.15s ease, box-shadow 0.15s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 22px rgba(15, 23, 42, 0.08);
    }
  }

  .icon {
    flex-shrink: 0;
    width: 36px;
    height: 36px;
    display: grid;
    place-items: center;
    border-radius: 10px;
    background: linear-gradient(135deg, #ccfbf1, #e0f2fe);
    color: var(--accent);
    font-size: 14px;
  }

  .label {
    display: block;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--muted);
    margin-bottom: 4px;
  }

  .value {
    display: block;
    font-size: 14px;
    font-weight: 600;
    line-height: 1.45;
    color: var(--ink);
  }
`;

const SectionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;

  .detail-section {
    position: relative;
    background: rgba(255, 255, 255, 0.92);
    border: 1px solid rgba(15, 118, 110, 0.1);
    border-radius: 16px;
    padding: 26px 26px 24px 28px;
    box-shadow: 0 8px 28px rgba(15, 23, 42, 0.05);
    overflow: hidden;

    &::before {
      content: "";
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 4px;
      background: linear-gradient(180deg, #14b8a6, #0ea5e9);
    }

    &:nth-child(even) {
      background: linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(240, 253, 250, 0.9));
    }
  }

  .section-header {
    display: flex;
    align-items: center;
    gap: 14px;
    margin-bottom: 16px;
    padding-bottom: 14px;
    border-bottom: 1px solid rgba(15, 118, 110, 0.12);
  }

  .section-index {
    flex-shrink: 0;
    width: 42px;
    height: 42px;
    display: grid;
    place-items: center;
    border-radius: 12px;
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.04em;
    color: var(--accent-dark);
    background: linear-gradient(135deg, #ccfbf1, #e0f2fe);
    border: 1px solid rgba(15, 118, 110, 0.15);
  }

  h2 {
    font-size: 1.2rem;
    font-weight: 700;
    margin: 0;
    color: var(--ink);
    line-height: 1.3;
  }

  .section-body {
    color: #334155;
    font-size: 15px;
    line-height: 1.75;
    padding-left: 2px;

    p {
      margin: 0 0 12px;

      &:last-child {
        margin-bottom: 0;
      }
    }

    ul,
    ol {
      padding-left: 0;
      margin: 0 0 12px;
      list-style: none;
    }

    li {
      position: relative;
      margin-bottom: 10px;
      padding: 10px 14px 10px 36px;
      background: rgba(241, 245, 249, 0.7);
      border-radius: 10px;
      border: 1px solid rgba(226, 232, 240, 0.9);

      &::before {
        content: "";
        position: absolute;
        left: 14px;
        top: 16px;
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: linear-gradient(135deg, #14b8a6, #0ea5e9);
      }
    }

    a {
      color: var(--accent);
      text-decoration: underline;
    }

    strong,
    b {
      color: var(--ink);
      font-weight: 600;
    }
  }
`;

const AsideColumn = styled.aside`
  @media (min-width: 980px) {
    position: sticky;
    top: 24px;
  }
`;

const EnrollmentCard = styled.div`
  background:
    linear-gradient(180deg, #ffffff 0%, #f0fdfa 100%);
  border: 1px solid rgba(15, 118, 110, 0.14);
  border-radius: 18px;
  padding: 20px;
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.1);

  .thumb-wrap {
    display: flex;
    justify-content: center;
    align-items: center;
    background: #f8fafc;
    border: 1px solid rgba(15, 118, 110, 0.1);
    border-radius: 12px;
    padding: 12px;
    margin-bottom: 18px;

    img {
      display: block;
      width: 140px;
      max-width: 100%;
      height: auto;
      max-height: 140px;
      object-fit: contain;
      border-radius: 8px;
    }
  }

  .price-block {
    margin-bottom: 22px;
    padding-bottom: 18px;
    border-bottom: 1px solid rgba(15, 118, 110, 0.12);
  }

  .price-label {
    display: block;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--muted);
    margin-bottom: 6px;
  }

  .price-amount {
    font-size: 2.25rem;
    font-weight: 700;
    color: var(--accent-dark);
    letter-spacing: -0.02em;
  }

  .field {
    margin-bottom: 18px;

    label {
      display: block;
      font-size: 13px;
      font-weight: 600;
      color: var(--ink);
      margin-bottom: 8px;
    }

    select {
      width: 100%;
      appearance: none;
      background: #f8fafc
        url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%2364748b' d='M1.41 0L6 4.58 10.59 0 12 1.41l-6 6-6-6z'/%3E%3C/svg%3E")
        no-repeat right 14px center;
      border: 1px solid #cbd5e1;
      border-radius: 10px;
      color: var(--ink);
      padding: 12px 36px 12px 14px;
      font-size: 14px;
      outline: none;
      transition: border-color 0.15s ease, box-shadow 0.15s ease;

      &:focus {
        border-color: var(--accent);
        box-shadow: 0 0 0 3px rgba(15, 118, 110, 0.15);
      }
    }
  }

  .schedule-note {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    background: rgba(204, 251, 241, 0.45);
    border: 1px solid rgba(15, 118, 110, 0.12);
    border-radius: 12px;
    padding: 12px 14px;
    margin-bottom: 18px;
    font-size: 13px;
    line-height: 1.45;
    color: #475569;

    svg {
      margin-top: 2px;
      color: var(--accent);
      flex-shrink: 0;
    }
  }

  .cart-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    width: 100%;
    background: linear-gradient(135deg, #0f766e, #0e7490);
    color: #fff;
    font-size: 15px;
    font-weight: 600;
    padding: 14px 18px;
    text-decoration: none;
    border: none;
    border-radius: 12px;
    transition: background 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease;
    box-shadow: 0 8px 20px rgba(15, 118, 110, 0.25);

    &:hover {
      background: linear-gradient(135deg, #0d5f59, #0c4a6e);
      transform: translateY(-1px);
      box-shadow: 0 12px 24px rgba(15, 118, 110, 0.3);
    }
  }

  .assurance {
    margin: 14px 0 0;
    text-align: center;
    font-size: 12px;
    color: var(--muted);
  }
`;

export default SingleTrainingDetail;
